import React, { FC, useCallback, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '@/atoms/Button'
import { FilledTicketIcon } from '@/atoms/Icons/FilledTicketIcon'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  isOpen: boolean
  onClose: () => void
  youtubeID: string
  buttonText?: string
}

export const OverlayYoutubeVideo: FC<Props> = ({ youtubeID, isOpen, onClose, buttonText }) => {
  const { t } = useTranslate('tickets')
  const ref = useRef<HTMLDivElement>(null)

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (event && ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [handleClickOutside])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[999999] flex items-center justify-center rounded-xl bg-black/95"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div onClick={(e) => e.stopPropagation()} ref={ref} className="w-full max-w-[900px]">
            <div className="aspect-h-9 aspect-w-16 flex w-full items-center justify-between rounded-xl bg-core-gray-950">
              <div className="lg:rounded-xl">
                <span className="aspect-h-9 aspect-w-16 block">
                  <EmbeddedVideo
                    url={`https://www.youtube-nocookie.com/embed/${youtubeID}`}
                    className="h-full w-full md:rounded-xl"
                  />
                </span>
              </div>
            </div>
            {isOpen && (
              <Button onClick={onClose} className="mx-auto mt-6 flex items-center gap-1" variant="white">
                <FilledTicketIcon color="core-gray-950" />
                {buttonText || t('viewGiveaway', 'View Giveaway')}
              </Button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
